// ---------------------------------------------------------------------------
// MTG deck-list parsing — isomorphic (no DOM, no Bun APIs).
//
// Accepts the common pasted formats:
//   4 Lightning Bolt
//   4x Lightning Bolt
//   4 Lightning Bolt (2X2) 117          (MTGA / Moxfield export, with set+cn)
//   SB: 2 Abrade                        (MTGO sideboard prefix)
//   1 Atraxa, Praetors' Voice *CMDR*    (commander marker)
// plus section headers ("Deck", "Sideboard", "Commander", "Companion",
// "About/Name <deck name>") and the header-less MTGA convention where a blank
// line separates mainboard from sideboard.
//
// Deck entries mirror the Proxy Table deck JSON shape: {name, qty, set, cn}.
// ---------------------------------------------------------------------------

export type DeckSection = "main" | "sideboard" | "commander" | "token";

export interface DeckEntry {
  qty: number;
  name: string;
  /** Set code, lowercased (e.g. "2x2"), when the list specifies a printing. */
  set?: string;
  /** Collector number within the set, when specified. */
  cn?: string;
  section: DeckSection;
}

export interface ParsedDeck {
  /** Deck name, when the list carries one (MTGA "About / Name …"). */
  name?: string;
  entries: DeckEntry[];
}

type InternalSection = DeckSection | "skip" | "about";

/** Section headers, normalized (lowercase, spaces removed, no colon/count). */
const SECTION_HEADERS: Record<string, InternalSection> = {
  deck: "main",
  main: "main",
  maindeck: "main",
  mainboard: "main",
  sideboard: "sideboard",
  side: "sideboard",
  companion: "sideboard",
  commander: "commander",
  commanders: "commander",
  maybeboard: "skip",
  considering: "skip",
  token: "token",
  tokens: "token",
  about: "about",
};

/** "Sideboard", "Sideboard:", "SIDEBOARD (15)", "Main deck" … → section. */
function headerFor(line: string): InternalSection | null {
  const norm = line
    .toLowerCase()
    .replace(/[:.]\s*$/, "")
    .replace(/\s*\(\d+\)\s*$/, "")
    .replace(/\s+/g, "");
  return SECTION_HEADERS[norm] ?? null;
}

/** One card line → entry (sans section), or null if it isn't a card line. */
function parseCardLine(line: string): Omit<DeckEntry, "section"> | null {
  // Trailing decorations: foil markers (*F*), tags ([Land]), etc.
  let rest = line.replace(/\s*(\*[A-Za-z]\*|\[[^\]]*\])\s*$/g, "").trim();
  let qty = 1;
  const qm = /^(\d+)\s*[xX]?\s+(.+)$/.exec(rest);
  if (qm) {
    qty = Math.min(99, Math.max(1, parseInt(qm[1]!, 10) || 1));
    rest = qm[2]!.trim();
  }
  if (!rest || !/[a-zA-Z]/.test(rest)) return null;

  // "Name (SET) CN" — set codes are 2-6 alphanumerics; cn is optional.
  const sm = /^(.+?)\s+\(([A-Za-z0-9]{2,6})\)(?:\s+(\S+))?\s*$/.exec(rest);
  if (sm) {
    const entry: Omit<DeckEntry, "section"> = { qty, name: sm[1]!.trim(), set: sm[2]!.toLowerCase() };
    if (sm[3]) entry.cn = sm[3];
    return entry;
  }
  return { qty, name: rest };
}

export function parseDeckList(text: string): ParsedDeck {
  const parsed: ParsedDeck = { entries: [] };
  let section: InternalSection = "main";
  let sawHeader = false;
  let sawMainCards = false;
  let blankSwitched = false;

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();

    // Blank line: MTGA header-less convention — the second block is the
    // sideboard. Only applies when no explicit headers are in play.
    if (!line) {
      if (!sawHeader && sawMainCards && section === "main" && !blankSwitched) {
        section = "sideboard";
        blankSwitched = true;
      }
      continue;
    }

    // Comments.
    if (line.startsWith("#") || line.startsWith("//")) continue;

    const header = headerFor(line);
    if (header) {
      section = header;
      sawHeader = true;
      continue;
    }

    if (section === "about") {
      const nm = /^name\s+(.+)$/i.exec(line);
      if (nm) parsed.name = nm[1]!.trim();
      continue;
    }
    if (section === "skip") continue;

    // Per-line overrides: "SB:" prefix, "*CMDR*" suffix.
    let lineSection: DeckSection = section;
    let body = line;
    if (/^sb:\s*/i.test(body)) {
      lineSection = "sideboard";
      body = body.replace(/^sb:\s*/i, "");
    }
    if (/\*CMDR\*\s*$/i.test(body)) {
      lineSection = "commander";
      body = body.replace(/\s*\*CMDR\*\s*$/i, "");
    }

    const entry = parseCardLine(body);
    if (!entry) continue;
    parsed.entries.push({ ...entry, section: lineSection });
    if (lineSection === "main") sawMainCards = true;
  }
  return parsed;
}

/** Physical card counts per section (client preview + server sanity checks). */
export function deckCounts(parsed: ParsedDeck): { main: number; sideboard: number; commander: number; token: number } {
  const c = { main: 0, sideboard: 0, commander: 0, token: 0 };
  for (const e of parsed.entries) c[e.section] += e.qty;
  return c;
}
